<template>
  <el-dialog
    :title="title"
    :model-value="visible"
    width="86%"
    top="4vh"
    destroy-on-close
    :close-on-click-modal="false"
    class="image-manager-dialog"
    @close="handleClose"
  >
    <div v-if="productMeta" class="product-info-header">
      <el-alert
        :title="productMeta.brand_name || ''"
        :description="metaDesc"
        type="info"
        :closable="false"
        show-icon
      />
    </div>

    <div class="toolbar">
      <el-input
        v-model="uploadColor"
        placeholder="颜色（可选）"
        clearable
        style="width:160px"
      />
      <el-input
        v-model="uploadVersion"
        placeholder="布料版型（可选）"
        clearable
        style="width:160px"
      />
      <el-upload
        :auto-upload="false"
        :show-file-list="false"
        multiple
        accept="image/*"
        :on-change="onFileSelect"
      >
        <el-button type="primary">选择图片上传到「{{ currentTypeLabel }}」</el-button>
      </el-upload>
      <el-button
        type="success"
        :loading="uploading"
        :disabled="!pendingFiles.length"
        @click="submitUpload"
      >上传 {{ pendingFiles.length || '' }}</el-button>
      <el-button @click="loadImages" :loading="loading">刷新</el-button>
    </div>

    <div v-if="pendingFiles.length" class="pending-bar">
      <span>待上传 {{ pendingFiles.length }} 张（{{ currentTypeLabel }}）</span>
      <el-button link type="danger" @click="pendingFiles = []">清空</el-button>
      <div class="pending-thumbs">
        <img v-for="f in pendingFiles" :key="f.id" :src="f.preview" alt="" />
      </div>
    </div>

    <div v-loading="loading">
      <el-empty v-if="!colors.length && !loading" description="暂无图片，请先上传">
      </el-empty>
      <template v-else>
        <el-tabs v-model="activeColor" @tab-change="onColorChange">
          <el-tab-pane
            v-for="c in colors"
            :key="c || '__empty__'"
            :label="c || '未分色'"
            :name="c || '__empty__'"
          />
        </el-tabs>
        <el-tabs v-model="activeType">
          <el-tab-pane
            v-for="type in imageTypes"
            :key="type.value"
            :label="`${type.label}(${countOf(type.value)})`"
            :name="type.value"
          >
            <div class="image-grid">
              <draggable
                v-if="listOf(type.value).length"
                :list="listOf(type.value)"
                item-key="id"
                class="images-container"
                :animation="180"
                ghost-class="sortable-ghost"
                @end="() => persistOrder(type.value)"
              >
                <template #item="{ element, index }">
                  <div class="image-item">
                    <el-image
                      :src="element.url"
                      fit="cover"
                      :preview-src-list="previewList(type.value)"
                      :initial-index="index"
                      :preview-teleported="true"
                      :hide-on-click-modal="true"
                      class="image"
                    />
                    <div class="image-order">{{ index + 1 }}</div>
                    <div class="image-actions">
                      <el-button
                        type="danger"
                        circle
                        size="small"
                        @click.stop="removeImage(type.value, index)"
                      >
                        <el-icon><Delete /></el-icon>
                      </el-button>
                    </div>
                    <div class="image-name" :title="element.name">{{ element.name }}</div>
                    <div class="drag-hint">拖拽排序</div>
                  </div>
                </template>
              </draggable>
              <div v-else class="no-images">暂无{{ type.label }}</div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete } from '@element-plus/icons-vue'
import draggable from 'vuedraggable'
import axios from 'axios'

const IMAGE_TYPES = [
  { label: '概念图', value: 'promotional' },
  { label: '设计图', value: 'design' },
  { label: '布料图', value: 'fabric' },
  { label: '成衣图', value: 'clothing' },
  { label: '模特图', value: 'model' },
  { label: '买家秀图', value: 'customer' }
]

export default {
  name: 'ImageViewer',
  components: { draggable, Delete },
  props: {
    visible: { type: Boolean, required: true },
    title: { type: String, default: '图片管理' },
    productId: { type: Number, default: null },
    productMeta: { type: Object, default: null },
    color: { type: String, default: '' }
  },
  emits: ['update:visible', 'close'],
  setup(props, { emit }) {
    const loading = ref(false)
    const uploading = ref(false)
    const activeColor = ref('__empty__')
    const activeType = ref('promotional')
    const uploadColor = ref('')
    const uploadVersion = ref('')
    const pendingFiles = ref([])
    // byColor[colorKey][type] = images[]
    const byColor = ref({})

    const imageTypes = IMAGE_TYPES
    const currentTypeLabel = computed(() => {
      const t = imageTypes.find(x => x.value === activeType.value)
      return t ? t.label : activeType.value
    })
    const metaDesc = computed(() => {
      const p = props.productMeta
      if (!p) return ''
      return `产品标题：${p.title || '—'} | 发布时间：${p.year || '—'}-${p.publish_month || '—'}`
    })
    const colors = computed(() => Object.keys(byColor.value))

    const colorKey = (c) => (c === '__empty__' || c == null ? '' : c)
    const listOf = (type) => {
      const ck = colorKey(activeColor.value)
      return (byColor.value[ck] && byColor.value[ck][type]) || []
    }
    const countOf = (type) => listOf(type).length
    const previewLists = ref({})
    const previewList = (type) => {
      const key = `${activeColor.value}|${type}`
      if (!previewLists.value[key]) {
        previewLists.value[key] = listOf(type).map(i => i.url)
      }
      return previewLists.value[key]
    }
    const rebuildPreview = () => {
      previewLists.value = {}
    }

    const loadImages = async () => {
      if (!props.productId) return
      loading.value = true
      try {
        const { data } = await axios.get(`/api/products/${props.productId}/images`)
        const payload = data.code === 200 ? data.data : data
        const grouped = {}
        Object.entries(payload || {}).forEach(([type, images]) => {
          ;(images || []).forEach((img) => {
            const c = img.color || ''
            if (!grouped[c]) grouped[c] = {}
            if (!grouped[c][type]) grouped[c][type] = []
            const url = img.url?.startsWith('http') ? img.url : encodeURI(img.url || '')
            grouped[c][type].push({
              id: img.id,
              url,
              name: img.name,
              order: img.order,
              color: c
            })
          })
        })
        // ensure empty color bucket exists for upload UX
        if (!Object.keys(grouped).length) grouped[''] = {}
        Object.keys(grouped).forEach((c) => {
          imageTypes.forEach((t) => {
            if (!grouped[c][t.value]) grouped[c][t.value] = []
            grouped[c][t.value].sort((a, b) => (a.order || 0) - (b.order || 0))
          })
        })
        byColor.value = grouped
        rebuildPreview()
        const prefer = props.color || uploadColor.value || ''
        if (grouped[prefer] != null) {
          activeColor.value = prefer || '__empty__'
        } else {
          const first = Object.keys(grouped)[0]
          activeColor.value = first === '' ? '__empty__' : first
        }
        uploadColor.value = colorKey(activeColor.value)
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载图片失败')
      } finally {
        loading.value = false
      }
    }

    const onColorChange = (name) => {
      uploadColor.value = colorKey(name)
      rebuildPreview()
    }

    const onFileSelect = (file) => {
      if (!file?.raw) return
      const reader = new FileReader()
      reader.onload = (e) => {
        pendingFiles.value.push({
          id: `${Date.now()}_${Math.random()}`,
          file: file.raw,
          name: file.name,
          preview: e.target.result
        })
      }
      reader.readAsDataURL(file.raw)
    }

    const submitUpload = async () => {
      if (!props.productId || !pendingFiles.value.length) return
      uploading.value = true
      try {
        const formData = new FormData()
        pendingFiles.value.forEach((f) => formData.append(activeType.value, f.file))
        const color = uploadColor.value || ''
        const ver = uploadVersion.value || ''
        await axios.post(
          `/api/products/${props.productId}/images?color=${encodeURIComponent(color)}&fabric_version=${encodeURIComponent(ver)}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        ElMessage.success(`已上传 ${pendingFiles.value.length} 张`)
        pendingFiles.value = []
        await loadImages()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '上传失败')
      } finally {
        uploading.value = false
      }
    }

    const persistOrder = async (type) => {
      const list = listOf(type)
      const order = list.map((i) => i.id).filter(Boolean)
      if (!order.length) return
      const color = colorKey(activeColor.value)
      try {
        await axios.put(
          `/api/products/${props.productId}/images/${encodeURIComponent(color || '_')}/${type}/order`,
          { order }
        )
        // keep local order fields
        list.forEach((img, idx) => { img.order = idx })
        rebuildPreview()
        ElMessage.success('顺序已保存')
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '保存顺序失败')
        loadImages()
      }
    }

    const removeImage = async (type, index) => {
      try {
        await ElMessageBox.confirm('确定删除这张图片？', '确认删除', { type: 'warning' })
        const color = colorKey(activeColor.value) || '_'
        await axios.delete(
          `/api/products/${props.productId}/images/${encodeURIComponent(color)}/${type}/${index}`
        )
        ElMessage.success('已删除')
        await loadImages()
      } catch (e) {
        if (e === 'cancel' || e === 'close') return
        ElMessage.error(e.response?.data?.message || '删除失败')
      }
    }

    const handleClose = () => {
      emit('update:visible', false)
      emit('close')
    }

    watch(
      () => props.visible,
      (v) => {
        if (v) {
          pendingFiles.value = []
          uploadColor.value = props.color || ''
          loadImages()
        }
      },
      { immediate: true }
    )

    return {
      loading, uploading, activeColor, activeType, uploadColor, uploadVersion,
      pendingFiles, imageTypes, currentTypeLabel, metaDesc, colors,
      listOf, countOf, previewList, loadImages, onColorChange, onFileSelect,
      submitUpload, persistOrder, removeImage, handleClose
    }
  }
}
</script>

<style scoped>
.product-info-header { margin-bottom: 12px; }
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}
.pending-bar {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
}
.pending-thumbs {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.pending-thumbs img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
}
.image-grid { min-height: 180px; padding: 8px 0; }
.images-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.image-item {
  position: relative;
  width: 180px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.image {
  width: 100%;
  height: 140px;
  display: block;
  cursor: zoom-in;
}
.image-order {
  position: absolute;
  top: 6px;
  left: 6px;
  background: rgba(0,0,0,.55);
  color: #fff;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 12px;
}
.image-actions {
  position: absolute;
  top: 6px;
  right: 6px;
}
.image-name {
  padding: 4px 8px;
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.drag-hint {
  text-align: center;
  font-size: 11px;
  color: #bbb;
  padding-bottom: 4px;
}
.no-images {
  text-align: center;
  color: #909399;
  padding: 48px 0;
  background: #fafafa;
  border-radius: 8px;
}
.sortable-ghost { opacity: 0.45; }
</style>
